import { inject, Injectable } from '@angular/core';
import { IAppState } from '@app/app.state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  EMPTY,
  filter,
  map,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { EInitEventTypeCode } from '@app/core/enums/init-event-type.enum';
import { SecurityEventsApiService } from '@app/core/api/security-events-api.service';
import { InitEventDisruptionApiService } from 'ngx-mt-ui-lib/api-client';
import { EventCardActions } from './event-card.actions';
import { ToastService } from 'ngx-mt-ui-lib/services/toast';
import { buildEventCardProtocolsSyncActions } from '../event-card-protocols-sync';
import { selectEventId, selectEventInfo } from './event-card.selectors';
import { isSameInstant } from './event-card-date.utils';

@Injectable()
export class EventCardEffects {
  private readonly store = inject(Store<IAppState>);
  private readonly actions$ = inject(Actions);
  private readonly translateService = inject(TranslateService);
  private readonly toastService = inject(ToastService);
  private readonly disruptionApiService = inject(InitEventDisruptionApiService);
  private readonly securityEventsApiService = inject(SecurityEventsApiService);

  readonly selectEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventCardActions.selectEvent),
        tap(({ id, eventType }) => {
          if (typeof id !== 'number' || !eventType) {
            return;
          }

          this.store.dispatch(
            EventCardActions.requestEventInfo({ id, eventType }),
          );

          if (eventType === EInitEventTypeCode.NNR) {
            this.store.dispatch(
              EventCardActions.requestEventPvsTaskList({ id }),
            );
            this.store.dispatch(EventCardActions.requestEventProtocols({ id }));
          }
        }),
      ),
    { dispatch: false },
  );

  readonly requestEventInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventCardActions.requestEventInfo),
      switchMap(({ id, eventType }) => {
        if (eventType === EInitEventTypeCode.NNR) {
          return this.disruptionApiService.getInfo(id).pipe(
            map((info) =>
              EventCardActions.requestEventInfoSuccess({ eventInfo: info }),
            ),
            catchError((error) =>
              of(EventCardActions.requestEventInfoError({ error })),
            ),
          );
        }

        return this.securityEventsApiService.getInfo(id).pipe(
          map((info) =>
            EventCardActions.requestEventInfoSuccess({ eventInfo: info }),
          ),
          catchError((error) =>
            of(EventCardActions.requestEventInfoError({ error })),
          ),
        );
      }),
    ),
  );

  readonly requestRefreshEventInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventCardActions.requestRefreshEventInfo),
      withLatestFrom(
        this.store.select(selectEventId),
        this.store.select(selectEventInfo),
      ),
      switchMap(([{ id, eventType }, selectedEventId, lastEventInfo]) => {
        if (id !== selectedEventId) {
          return EMPTY;
        }

        if (eventType === EInitEventTypeCode.NNR) {
          return this.disruptionApiService.getInfo(id).pipe(
            map((info) =>
              EventCardActions.requestRefreshEventInfoSuccess({
                eventInfo: info,
                lastEventInfo,
              }),
            ),
            catchError((error) =>
              of(EventCardActions.requestRefreshEventInfoError({ error })),
            ),
          );
        }

        return this.securityEventsApiService.getInfo(id).pipe(
          map((info) =>
            EventCardActions.requestRefreshEventInfoSuccess({
              eventInfo: info,
              lastEventInfo,
            }),
          ),
          catchError((error) =>
            of(EventCardActions.requestRefreshEventInfoError({ error })),
          ),
        );
      }),
    ),
  );

  readonly requestRefreshEventInfoSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventCardActions.requestRefreshEventInfoSuccess),
        withLatestFrom(this.store.select(selectEventId)),
        filter(
          ([{ eventInfo }, selectedEventId]) =>
            eventInfo?.id === selectedEventId,
        ),
        tap(([{ eventInfo }]) => {
          if (eventInfo?.initEventTypeCode === EInitEventTypeCode.NNR) {
            this.store.dispatch(
              EventCardActions.requestEventProtocols({ id: eventInfo.id }),
            );
          }
        }),
      ),
    { dispatch: false },
  );

  readonly requestEventPvsTaskList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventCardActions.requestEventPvsTaskList),
      switchMap(({ id }) =>
        this.disruptionApiService.getTaskList(id).pipe(
          map((list) =>
            EventCardActions.requestEventPvsTaskListSuccess({
              eventPvsTaskList: list,
            }),
          ),
          catchError((error) =>
            of(EventCardActions.requestEventPvsTaskListError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestEventProtocols$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventCardActions.requestEventProtocols),
      withLatestFrom(this.store.select(selectEventId)),
      switchMap(([{ id }, selectedEventId]) => {
        if (id !== selectedEventId) {
          return EMPTY;
        }

        return this.disruptionApiService.getProtocols(id).pipe(
          tap((protocols) => {
            buildEventCardProtocolsSyncActions(
              id,
              protocols,
              this.translateService,
            ).forEach((action) => this.store.dispatch(action));
          }),
          map(() => EventCardActions.requestEventProtocolsSuccess()),
          catchError((error) =>
            of(EventCardActions.requestEventProtocolsError({ error })),
          ),
        );
      }),
    ),
  );

  readonly requestEventActiveProtocolGantt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventCardActions.requestEventActiveProtocolGantt),
      switchMap(({ eventId, protocolId, protocolLaunchDate }) =>
        this.disruptionApiService.getGantt(eventId).pipe(
          map((gantt) =>
            isSameInstant(gantt?.launchDate, protocolLaunchDate)
              ? EventCardActions.requestEventActiveProtocolGanttSuccess({
                  eventId,
                  protocolId,
                  protocolLaunchDate,
                  gantt,
                })
              : EventCardActions.discardEventActiveProtocolGantt({
                  eventId,
                  protocolId,
                  protocolLaunchDate,
                }),
          ),
          catchError((error) =>
            of(
              EventCardActions.requestEventActiveProtocolGanttError({
                eventId,
                protocolId,
                protocolLaunchDate,
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly showErrors$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          EventCardActions.requestEventInfoError,
          EventCardActions.requestEventProtocolsError,
        ),
        tap((action) => {
          const error = action.error?.error;
          let errorText: string = error?.message ?? error.error ?? '';
          error?.details
            ?.filter((detail) => !!detail.message)
            ?.forEach((detail) => {
              errorText += `
                ${detail.message}`;
            });
          let errorTitle: string = null;

          switch (action.type) {
            case '[EVENT_CARD] requestEventInfoError':
              errorTitle = this.translateService.instant(
                'EVENT_CARD.FUNCTIONS.LOAD_INFO.ERROR',
              );
              break;
            case '[EVENT_CARD] requestEventProtocolsError':
              errorTitle = this.translateService.instant(
                'GENERAL.ERROR_REQUEST',
              );
              break;
            default:
              errorTitle = this.translateService.instant(
                'GENERAL.ERROR_REQUEST',
              );
              break;
          }

          this.toastService.add({
            level: 'error',
            summary: errorTitle,
            detail: errorText,
          });
        }),
      ),
    { dispatch: false },
  );
}
